import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DeploymentAreaDerivedPopulationMetrics } from "@situate/contracts";
import type {
  AreaPopulationResponse,
  AreaPopulationThresholds,
  TotalPopulationTrendResponse,
  TotalPopulationTrendUnconfiguredArea,
} from "@situate/contracts/runtime";
import { areaPopulationResponseSchema } from "@situate/contracts/runtime-zod";
import { formatAnalyticsPeriodLabel } from "@/features/deployment-dashboard/shared/analytics-query-range";
import {
  deriveDutyFreePopulationTrend,
  getTotalPopulationTrendQueryRange,
} from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics/population-trend-analytics-chart.utils";
import type { PopulationTrendAnalyticsPoint } from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics/types";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

const TOTAL_POPULATION_TREND_PATH = "/v1/analytics/total-population-trend";
const AREA_POPULATION_PATH = "/v1/analytics/area-population";

interface PopulationTrendAnalyticsState {
  data: PopulationTrendAnalyticsPoint[];
  loading: boolean;
  error: Error | null;
  requestedAreaIds: readonly string[];
  configuredAreaIds: readonly string[];
  unconfiguredAreas: readonly TotalPopulationTrendUnconfiguredArea[];
  thresholds: AreaPopulationThresholds | null;
  dutyFreeData?: PopulationTrendAnalyticsPoint[];
}

interface UsePopulationTrendAnalyticsParams {
  deploymentId: string;
  /** Areas to aggregate; empty while areas are still loading. */
  areaIds: readonly string[];
  startDate: Date;
  endDate: Date;
  enabled?: boolean;
  derivedPopulationMetrics?: DeploymentAreaDerivedPopulationMetrics | null;
}

/**
 * Fetches the Analytics page's "Population Trend" chart data from the
 * runtime API's `total-population` endpoint, for the selected areas and date
 * range.
 *
 * This is Total Population (baseline_population + cumulative ingress -
 * cumulative egress, reconstructed from configured throughput line
 * boundaries), a distinct analytics path from Live View / Chart View's
 * tracked/occupancy Population, which is unaffected by this hook.
 */
export function usePopulationTrendAnalytics({
  deploymentId,
  areaIds,
  startDate,
  endDate,
  enabled = true,
  derivedPopulationMetrics,
}: UsePopulationTrendAnalyticsParams): PopulationTrendAnalyticsState {
  const runtime = useRuntimeHttpClient();
  const hasTotalPopulationGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "analytics_total_population_trend"
  );
  const hasAreaPopulationGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "analytics_area_population"
  );
  const client = runtime.client;
  const { granularity, startTime, endTime } = getTotalPopulationTrendQueryRange(startDate, endDate);

  const query = useQuery<TotalPopulationTrendResponse>({
    queryKey: ["population-trend-analytics", deploymentId, areaIds, startTime, endTime],
    queryFn: async ({ signal }) => {
      if (client == null) throw new Error("Runtime client is unavailable");

      const response = await client.post<TotalPopulationTrendResponse>(
        TOTAL_POPULATION_TREND_PATH,
        {
          area_ids: areaIds,
          start_time: startTime,
          end_time: endTime,
          granularity,
        },
        { signal }
      );

      return response;
    },
    enabled:
      Boolean(client) &&
      runtime.deploymentId === deploymentId &&
      hasTotalPopulationGrant &&
      enabled &&
      areaIds.length > 0,
  });

  const thresholdsQuery = useQuery<AreaPopulationResponse>({
    queryKey: ["population-trend-analytics-thresholds", deploymentId, areaIds],
    queryFn: async ({ signal }) => {
      if (client == null) throw new Error("Runtime client is unavailable");

      const response = await client.post<AreaPopulationResponse>(
        AREA_POPULATION_PATH,
        {
          area_ids: areaIds,
          start_time: startTime,
          end_time: endTime,
          granularity,
        },
        { signal }
      );

      // Runtime responses are untrusted; validate the shared response before using its projection.
      return areaPopulationResponseSchema.parse(response);
    },
    enabled:
      Boolean(client) &&
      runtime.deploymentId === deploymentId &&
      hasAreaPopulationGrant &&
      enabled &&
      areaIds.length > 0,
  });

  const data = useMemo(() => {
    return (query.data?.data_points ?? []).map((point) => {
      return {
        period: formatAnalyticsPeriodLabel(point.window_start, granularity),
        timestamp: point.window_start,
        averageTotalPopulation: point.average_total_population,
        peakTotalPopulation: point.peak_total_population,
      };
    });
  }, [query.data, granularity]);
  const dutyFreeData = useMemo(
    () =>
      derivedPopulationMetrics?.duty_free_population != null
        ? deriveDutyFreePopulationTrend(data, thresholdsQuery.data?.data_points ?? [])
        : [],
    [data, derivedPopulationMetrics, thresholdsQuery.data]
  );

  return {
    data,
    loading: query.isLoading,
    error: query.error,
    requestedAreaIds: areaIds,
    configuredAreaIds: query.data?.area_ids ?? [],
    unconfiguredAreas: query.data?.unconfigured_areas ?? [],
    thresholds: thresholdsQuery.data?.thresholds ?? null,
    dutyFreeData,
  };
}
